/**
 * AIForumPostHelper Component - AI assistant for writing forum posts
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Sparkles, Loader2, Copy, Check, Wand2 } from 'lucide-react';
import { useAIGeneration, getErrorMessage, safeString } from './utils';

export const AIForumPostHelper = ({ categoryName, onInsert }) => {
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState('');
  const [postType, setPostType] = useState('discussion');
  const [generatedTitle, setGeneratedTitle] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  
  const { loading, copied, generate, handleCopy } = useAIGeneration('forum-post');
  
  const safeCategory = safeString(categoryName, 'General');
  
  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast.error('Please describe what you want to post about');
      return;
    }
    
    try {
      const response = await generate({
        category_name: safeCategory,
        topic: topic,
        post_type: postType
      });
      setGeneratedTitle(response.generated_title || '');
      setGeneratedContent(response.generated_content || response.generated_text || '');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate post'));
    }
  };
  
  const handleInsert = () => {
    if (onInsert) onInsert({ title: generatedTitle, content: generatedContent });
    setOpen(false);
    setGeneratedTitle('');
    setGeneratedContent('');
    setTopic('');
    toast.success('Post content inserted');
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Sparkles className="w-4 h-4 text-purple-500" />
          AI Assist
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-purple-500" />
            AI Post Writer
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <Card className="bg-slate-100">
            <CardContent className="pt-4">
              <p className="text-sm text-gray-600 mb-1">Writing for category:</p>
              <p className="font-medium">{safeCategory}</p>
            </CardContent>
          </Card>
          
          <div>
            <Label>What would you like to post about?</Label>
            <Textarea
              placeholder="E.g., Share my experience with meditation this week, ask about chakra balancing techniques..."
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              rows={3}
              className="mt-1"
            />
          </div>
          
          <div>
            <Label>Post Type</Label>
            <Select value={postType} onValueChange={setPostType}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="discussion">Start a Discussion</SelectItem>
                <SelectItem value="question">Ask a Question</SelectItem>
                <SelectItem value="sharing">Share an Experience</SelectItem>
                <SelectItem value="announcement">Make an Announcement</SelectItem>
                <SelectItem value="resource">Share a Resource/Insight</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Button 
            onClick={handleGenerate} 
            disabled={loading || !topic.trim()}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> Generate Post</>
            )}
          </Button>
          
          {(generatedTitle || generatedContent) && (
            <Card className="bg-slate-50">
              <CardContent className="pt-4">
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-sm text-gray-600">Generated Post:</Label>
                  <Button variant="ghost" size="sm" onClick={() => handleCopy(`${generatedTitle}\n\n${generatedContent}`)}>
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                {generatedTitle && (
                  <div className="mb-2">
                    <p className="text-xs text-gray-500 mb-1">Title:</p>
                    <p className="font-medium text-sm bg-white p-2 rounded border">{generatedTitle}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-500 mb-1">Content:</p>
                  <div className="whitespace-pre-wrap text-sm bg-white p-3 rounded border max-h-48 overflow-y-auto">
                    {generatedContent}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        
        {(generatedTitle || generatedContent) && (
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setGeneratedTitle(''); setGeneratedContent(''); }}>
              Regenerate
            </Button>
            <Button onClick={handleInsert} className="bg-cyan-600 hover:bg-cyan-700">
              Use This Post
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
